// Push notifications in the iOS / Android app (backend/utils/push.py).
//
//   startPush()     after sign-in: this phone's token goes to the backend and a tapped
//                   notification opens its page (a tap that started the app on Android
//                   leaves the page in the preferences: native/intents.js)
//   askForPush()    asks once, in context (after the person first sends work to a bot),
//                   with a short explanation first (PushPromptDialog); never at launch
//   stopPush()      on sign-out: the backend forgets this phone
//   reconcile()     on start, on every return to the app and when something is settled on
//                   another screen: notifications the backend no longer counts as open
//                   (GET /notifications/open, backend utils/notify.py) leave the phone. Each
//                   push's nid is its iOS identifier (apns-collapse-id) and its Android tag;
//                   Android also takes them off at once (BusyBeeMessagingService dismiss),
//                   iOS has no reliable way to do that from the server.
// Buttons on notifications (reply, approve, reject) are native code (PushActions.swift,
// NotificationActionReceiver.java): they work without the web app running.
import { App as CapApp } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { mobileApi, notificationsApi } from '../services/api';
import { isNative, platform } from './platform';

const RESOLVED_EVENT = 'bb:notifications-resolved';  // contexts/NotificationsContext.jsx

const TOKEN_KEY = 'bb_push_token';
const ASKED_KEY = 'bb_push_asked';
export const PUSH_ASK_EVENT = 'bb:push-ask';
// TestFlight and the App Store use production APNs; Xcode debug builds the sandbox.
const APNS_ENV = process.env.REACT_APP_APNS_ENV === 'sandbox' ? 'sandbox' : 'production';

let started = false;
let offResolved = null;

// Our notifications carry their nid: the identifier on iOS, the tag on Android.
const nidOf = (n) => (platform() === 'ios' ? (n?.data?.bb?.nid ? n.id : null) : n?.tag) || null;

/** Take off the phone what the backend says is settled (all but `keep`, or exactly `drop`). */
async function removeDelivered({ keep = null, drop = null } = {}) {
  const { notifications = [] } = await PushNotifications.getDeliveredNotifications();
  const gone = notifications.filter((n) => {
    const nid = nidOf(n);
    if (!nid) return false;  // from before notifications had one: left alone
    return drop ? drop.has(nid) : !keep.has(nid);
  });
  if (gone.length) {
    await PushNotifications.removeDeliveredNotifications({
      notifications: gone.map((n) => (platform() === 'ios' ? { id: n.id } : { id: n.id, tag: n.tag })),
    });
  }
}

export async function reconcile() {
  if (!isNative() || !started) return;
  try {
    const { data } = await notificationsApi.open();
    await removeDelivered({ keep: new Set(data?.nids || []) });
  } catch {
    // offline, or the plugin not ready yet: the next return to the app tries again
  }
}

// The language native code uses for button titles and channel names.
export async function rememberLanguage(lang) {
  if (isNative() && lang) await Preferences.set({ key: 'bb_lang', value: lang });
}

export async function pushPermission() {
  if (!isNative()) return 'denied';
  const { receive } = await PushNotifications.checkPermissions();
  return receive; // 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
}

// Android without Firebase (a build without google-services.json) would close on
// register(); MainActivity says whether Firebase is there.
async function canRegister() {
  if (platform() !== 'android') return true;
  return (await Preferences.get({ key: 'bb_push_ready' })).value === '1';
}

export async function enablePush() {
  if (!isNative()) return false;
  const { receive } = await PushNotifications.requestPermissions();
  if (receive !== 'granted') return false;
  if (await canRegister()) await PushNotifications.register();
  return true;
}

export async function askForPush() {
  if (!isNative()) return;
  const asked = await Preferences.get({ key: ASKED_KEY });
  if (asked.value) return;
  if (!(await pushPermission()).startsWith('prompt')) return;
  await Preferences.set({ key: ASKED_KEY, value: '1' });
  window.dispatchEvent(new CustomEvent(PUSH_ASK_EVENT));
}

const urlOf = (notification) => {
  const data = notification?.data || {};
  return data.bb?.url || data.url || null; // iOS: {bb: {...}}, Android: flat data
};

export async function startPush(open) {
  if (!isNative() || started) return;
  started = true;
  await PushNotifications.addListener('registration', async ({ value }) => {
    await Preferences.set({ key: TOKEN_KEY, value });
    const info = await CapApp.getInfo().catch(() => ({}));
    await mobileApi.registerDevice({
      platform: platform(), token: value, app_version: info.version,
      environment: platform() === 'ios' ? APNS_ENV : 'production',
    }).catch(() => {});
  });
  await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const url = urlOf(notification);
    if (url) open(url);
  });
  if ((await pushPermission()) === 'granted' && (await canRegister())) await PushNotifications.register(); // the token may change
  // Settled while the app is open (the bell's live stream): off the phone now.
  const onResolved = (e) => {
    const drop = new Set(e.detail?.nids || []);
    if (drop.size) removeDelivered({ drop }).catch(() => {});
  };
  window.addEventListener(RESOLVED_EVENT, onResolved);
  const resume = await CapApp.addListener('resume', () => { reconcile(); });
  offResolved = () => {
    window.removeEventListener(RESOLVED_EVENT, onResolved);
    resume.remove();
  };
  reconcile();
}

export async function stopPush(authToken) {
  if (!isNative()) return;
  const { value } = await Preferences.get({ key: TOKEN_KEY });
  if (value) await mobileApi.unregisterDevice(value, authToken).catch(() => {});
  await Preferences.remove({ key: TOKEN_KEY });
  // Signed out: nothing of this account stays on the phone.
  await PushNotifications.removeAllDeliveredNotifications().catch(() => {});
  await PushNotifications.removeAllListeners().catch(() => {});
  offResolved?.();
  offResolved = null;
  started = false;
}
